import React from "react";
import TableSkeleton from "./TableSkeleton";
import { Box, Button } from "@mui/material";

import { displayFont, monoFamily, tokens, radius } from "../theme";

/**
 * The three states a list has when it is not full of data.
 *
 * From the module-screens book, which draws all four states of the expenses
 * queue and says the same set applies to every module. Loading is not here —
 * that is <TableSkeleton>, because a skeleton has to match the shape of the
 * table it stands in for.
 *
 *   empty      Nothing to show. A dashed frame: the container exists, its
 *              contents do not yet.
 *   error      The read failed. A solid frame, and deliberately no red and no
 *              warning triangle — a failed read is not the user's mistake. It
 *              says plainly that nothing was lost and carries a reference the
 *              user can quote to whoever can look it up.
 *   forbidden  Not yours to see. A solid frame on the page ground, naming the
 *              person who holds the permission, because the next thing anyone
 *              does is go and ask them. It never reads as an error, and it
 *              always offers the version of the screen they *can* see.
 *
 * On wording, the book asks for two kinds of empty and they are not
 * interchangeable. "Nothing yet", on a new account, offers the first action —
 * "No claims yet" / "Submit a claim". "Nothing left", as when a queue has been
 * worked, says the job is done and points at the nearest useful screen —
 * "Nothing waiting on you" / "See approved claims". Same component, different
 * sentence; picking the wrong one tells a new user their work is finished.
 *
 *   <EmptyState title="Nothing waiting on you"
 *               description="Every claim for March has been approved."
 *               secondaryLabel="See approved claims" onSecondary={…}
 *               actionLabel="Submit a claim" onAction={…} />
 *
 *   <EmptyState variant="error" title="These claims could not be loaded"
 *               onRetry={load} reference="EXP-5F31A9" />
 *
 *   <EmptyState variant="forbidden" title="You can see your own claims only"
 *               description="Company-wide claims are limited to Finance. Alex Reyes can grant access."
 *               secondaryLabel="Request access" actionLabel="My claims" onAction={…} />
 */

const FRAMES = {
  // Dashed: the shape is real, the contents are not here yet.
  empty: {
    border: `1px dashed ${tokens.borderStrong}`,
    background: "transparent",
  },
  error: {
    border: `1px solid ${tokens.border}`,
    background: "transparent",
  },
  // The page ground rather than the card, which sets it back a step: this is
  // not a thing that went wrong, it is a boundary.
  forbidden: {
    border: `1px solid ${tokens.border}`,
    background: tokens.surface,
  },
};

// 9px/18px and 13px are the book's own in-frame action metrics, smaller than a
// page-level button on purpose — these sit inside a panel, not above one.
const actionSx = {
  borderRadius: "8px",
  padding: "9px 18px",
  fontSize: "0.8125rem",
  fontWeight: 600,
  textTransform: "none",
  minHeight: "auto",
  lineHeight: 1.5,
};

const EmptyState = ({
  title = "Nothing here yet",
  description,
  icon,
  variant = "empty", // "empty" | "error" | "forbidden"
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
  onRetry,
  reference,
}) => {
  const frame = FRAMES[variant] || FRAMES.empty;
  const isError = variant === "error";

  /**
   * A failed read defaults to the book's reassurance rather than leaving the
   * title to stand alone. The title says what did not happen; this says what it
   * costs, which is nothing — and that is the part that stops someone
   * re-submitting a form they think they lost.
   */
  const body =
    description ||
    (isError
      ? reference
        ? "Nothing has been lost and nothing has changed. Try again, and if it keeps failing send your HR team this reference."
        : "Nothing has been lost and nothing has changed. Try again — if it keeps failing, your HR team can look into it."
      : undefined);

  // Existing callers pass `onAction` alone with variant="error" and mean retry.
  const retryHandler = onRetry || (isError ? onAction : undefined);
  const primaryLabel = retryHandler ? actionLabel || "Try again" : actionLabel;
  const primaryHandler = retryHandler || onAction;

  return (
    <Box
      sx={{
        width: "100%",
        // It stands in for a card band (it replaces the table), so it takes
        // the frame's card radius rather than the panel one.
        borderRadius: `${radius.card}px`,
        padding: "44px 28px",
        textAlign: "center",
        ...frame,
      }}
    >
      {/* No glyph on a failure: the book rules out the warning triangle, and an
          icon is the fastest way for one to creep back in. */}
      {!isError && icon}

      <Box
        sx={{
          fontFamily: displayFont,
          fontWeight: 700,
          fontSize: "1.0625rem",
          color: tokens.text,
          marginBottom: "7px",
        }}
      >
        {title}
      </Box>

      {body && (
        <Box
          sx={{
            fontSize: "0.8125rem",
            color: tokens.textMuted,
            lineHeight: 1.6,
            maxWidth: "20rem",
            margin: "0 auto 20px",
          }}
        >
          {body}
        </Box>
      )}

      {(primaryLabel || secondaryLabel || reference) && (
        <Box
          sx={{
            display: "inline-flex",
            gap: "10px",
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "center",
            // Restores the gap the description would have left when there is
            // no description to leave it.
            marginTop: body ? 0 : "20px",
          }}
        >
          {/* Secondary sits to the left of the primary, everywhere. */}
          {secondaryLabel && onSecondary && (
            <Button
              onClick={onSecondary}
              sx={{
                ...actionSx,
                border: `1px solid ${tokens.borderStrong}`,
                color: tokens.primary,
                "&:hover": { background: tokens.surfaceSubtle },
              }}
            >
              {secondaryLabel}
            </Button>
          )}

          {primaryLabel && primaryHandler && (
            <Button
              onClick={primaryHandler}
              sx={{
                ...actionSx,
                background: tokens.primary,
                color: tokens.card,
                "&:hover": { background: tokens.primaryHover },
              }}
            >
              {primaryLabel}
            </Button>
          )}

          {/* Monospace, because its whole job is to be read back character by
              character to somebody who can look it up. */}
          {reference && (
            <Box
              component="code"
              sx={{
                fontFamily: monoFamily,
                fontSize: "0.75rem",
                color: tokens.textMuted,
                background: tokens.surface,
                border: `1px solid ${tokens.border}`,
                borderRadius: "6px",
                padding: "8px 12px",
              }}
            >
              {reference}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EmptyState;
